/**
 * TemplateRulesWidget — Collapsible rules section with sentence-builder CRUD.
 *
 * Reads rules from formData child table, renders summary cards with edit/delete,
 * opens RuleSheet for add/edit, writes back via onFieldChange("rules", newArray).
 *
 * Plan: 126-template-detail-page, Epic 03
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import { ChevronDown, Plus, Pencil, X, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { RuleSheet } from "../components/RuleSheet";
import type { TemplateRule, TemplateField } from "../api/customizationAdminTypes";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { SectionHeader } from "@/features/inventory/item-detail/components/SectionHeader";
import { useLanguage } from "@/i18n/useLanguage";
import { getSectionTitleTranslation } from "@/lib/views/labels/translations";

const i18n = {
  addRule: { en: "Add Rule", th: "เพิ่มกฎ" },
  noRules: { en: "No rules", th: "ไม่มีกฎ" },
  rulesCount: { en: (n: number) => `${n} rule${n !== 1 ? "s" : ""}`, th: (n: number) => `${n} กฎ` },
  noRulesYet: { en: "No rules yet", th: "ยังไม่มีกฎ" },
  rulesHelp: {
    en: "Rules let you show or hide fields based on customer choices. Most templates don't need rules.",
    th: "กฎช่วยให้คุณแสดงหรือซ่อนฟิลด์ตามตัวเลือกของลูกค้า แม่แบบส่วนใหญ่ไม่ต้องใช้กฎ",
  },
} as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function withIdx<T extends Record<string, unknown>>(rows: T[]): T[] {
  return rows.map((row, i) => ({ ...row, idx: i + 1 }));
}

function makeLocalId(): string {
  return `__new_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

// ---------------------------------------------------------------------------
// Main widget
// ---------------------------------------------------------------------------

type RuleRow = TemplateRule & { _localId?: string; [key: string]: unknown };

export function TemplateRulesWidget({
  formData,
  onFieldChange,
  resetKey,
}: ExperienceWidgetProps) {
  const { language } = useLanguage();
  const t = (key: keyof typeof i18n) => {
    const v = i18n[key][language] || i18n[key].en;
    return typeof v === "string" ? v : "";
  };

  const rules = (formData.rules || []) as RuleRow[];
  const fields = (formData.fields || []) as TemplateField[];

  // -- Local UI state --
  const [expanded, setExpanded] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  useEffect(() => {
    setSheetOpen(false);
    setEditingIndex(null);
  }, [resetKey]);

  const editRule = useMemo(
    () => (editingIndex !== null ? rules[editingIndex] ?? null : null),
    [editingIndex, rules],
  );

  // -- Handlers --

  const handleAdd = useCallback(() => {
    setEditingIndex(null);
    setSheetOpen(true);
    if (!expanded) setExpanded(true);
  }, [expanded]);

  const handleEdit = useCallback((index: number) => {
    setEditingIndex(index);
    setSheetOpen(true);
  }, []);

  const handleDelete = useCallback(
    (index: number) => {
      const updated = rules.filter((_, i) => i !== index);
      onFieldChange("rules", withIdx(updated));
    },
    [rules, onFieldChange],
  );

  const handleRuleSave = useCallback(
    (rule: TemplateRule) => {
      let updated: RuleRow[];

      if (editingIndex !== null) {
        const existing = rules[editingIndex];
        updated = rules.map((r, i) =>
          i === editingIndex ? { ...existing, ...rule } : r,
        );
        setSheetOpen(false);
        setEditingIndex(null);
      } else {
        const newRow: RuleRow = { ...rule, _localId: makeLocalId() };
        updated = [...rules, newRow];
      }

      onFieldChange("rules", withIdx(updated));
    },
    [editingIndex, rules, onFieldChange],
  );

  const rulesCountText = rules.length > 0
    ? i18n.rulesCount[language](rules.length)
    : t("noRules");

  // -- Render --

  return (
    <div>
      {/* Section header row with action button on the right */}
      <div className="flex items-end justify-between mb-3">
        <SectionHeader label={getSectionTitleTranslation("Rules", language)} icon={Settings} color="orange" />
        <Button
          variant="outline"
          size="sm"
          onClick={handleAdd}
          className="shrink-0 mb-0.5"
        >
          <Plus className="h-4 w-4 mr-1" />
          {t("addRule")}
        </Button>
      </div>

      <div className="rounded-2xl overflow-hidden bg-card shadow-apple p-6">
        {/* Collapsible header */}
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex w-full items-center justify-between text-left"
          aria-expanded={expanded}
        >
          <span className="text-sm font-medium text-foreground">
            {rulesCountText}
          </span>
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              expanded && "rotate-180",
            )}
          />
        </button>

        {/* Collapsible content */}
        {expanded && (
          <div className="mt-4">
            {rules.length === 0 ? (
              <div className="border-2 border-dashed border-muted-foreground/20 rounded p-6 text-center text-muted-foreground">
                <p className="text-sm">{t("noRulesYet")}</p>
                <p className="mt-1 max-w-xs mx-auto text-xs">{t("rulesHelp")}</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={handleAdd}>
                  <Plus className="h-4 w-4 mr-1" />
                  {t("addRule")}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {rules.map((rule, i) => (
                  <div
                    key={rule.name || rule._localId || `rule-${i}`}
                    className="group flex items-center gap-2 rounded-sm border bg-card p-3"
                  >
                    <p className="flex-1 text-sm italic text-muted-foreground">
                      {rule.summary ||
                        `If ${rule.when_field} ${rule.operator} ${rule.has_value}, ${rule.then_action.toLowerCase()} ${rule.target_field}`}
                    </p>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100"
                      onClick={() => handleEdit(i)}
                      aria-label={`Edit rule ${i + 1}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-destructive opacity-0 group-hover:opacity-100"
                      onClick={() => handleDelete(i)}
                      aria-label={`Delete rule ${i + 1}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Rule sheet (add/edit) */}
        <RuleSheet
          open={sheetOpen}
          onOpenChange={(open) => {
            setSheetOpen(open);
            if (!open) setEditingIndex(null);
          }}
          editRule={editRule}
          fields={fields}
          onSave={handleRuleSave}
        />
      </div>
    </div>
  );
}
